import { SortOrder } from '@/constants/request';
import { CASES_LIST_TAG, LIST_QUERY_CACHE_TTL_SECONDS } from '@/api';
import { api } from '@/api';

export type CaseSource = 'PROCLAIM' | 'UPLOAD';

export interface CaseData {
  caseReference: string;
  department: string;
  client: string;
  defendant: string;
  fileHandler: string;
  team: string;
  lastQueriedDatetime: string;
  lastSyncedDatetime: string;
  matterId: string;
  source: CaseSource;
}

export interface GetCasesResponse {
  matters: CaseData[];
  numberOfResults: number;
}

export type GetCasesRequest = {
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: SortOrder;
  search?: string;
  departments?: string[];
  clients?: string[];
  defendants?: string[];
  fileHandlers?: string[];
  teams?: string[];
  sources?: CaseSource[];
};

export function buildGetCasesQueryConfig({
  pageIndex,
  pageSize,
  sortBy = 'lastSynced',
  sortOrder = 'desc',
  search,
  departments,
  clients,
  defendants,
  fileHandlers,
  teams,
  sources,
}: GetCasesRequest) {
  const params: Record<string, unknown> = {
    page_index: pageIndex,
    page_size: pageSize,
    sort_by: sortBy === 'lastQueriedDatetime' ? 'last_queried' : 'last_synced',
    sort_direction: sortOrder === 'asc' ? 'ascending' : 'descending',
    department_name: departments,
    client_name: clients,
    defendant: defendants,
    file_handler_name: fileHandlers,
    team_name: teams,
    source: sources,
    search_term: search ?? undefined,
  };

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') {
      delete params[k];
    }
  }

  return {
    url: '/matters',
    method: 'GET' as const,
    params,
  };
}

const getCasesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCases: builder.query<GetCasesResponse, GetCasesRequest>({
      query: (args) => buildGetCasesQueryConfig(args),
      providesTags: (_result, _error, { pageIndex, pageSize }) => [
        { type: CASES_LIST_TAG, id: `${pageIndex}-${pageSize}` },
      ],
      extraOptions: { maxRetries: 3 },
      keepUnusedDataFor: LIST_QUERY_CACHE_TTL_SECONDS,
    }),
  }),
});

export const { useLazyGetCasesQuery, useGetCasesQuery } = getCasesApi;
